/**
 * Renders announcements from announcements.ts as banners in the dashboard.
 * Each entry is date-filtered and optionally dismissible via localStorage.
 * The Banner component is also exported for use by hardcoded banners in DashboardLayout.
 */
import { tokens } from "@blurb/codex-react";
import { CloseButton } from "@mantine/core";
import { Flex, IconV3, RpiLink, RpiText } from "@rpi/openapi-core";
import React from "react";
import { Link } from "react-router-dom";
import {
  type Announcement,
  type AnnouncementVariant,
  getActiveAnnouncements,
  dismissAnnouncement,
  isNotDismissed,
} from "../../announcements";
import { optObj } from "../../utils/misc.util";

/** Codex Alert semantic tokens mapped to announcement variants. */
export const announcementVariantStyles: Record<
  AnnouncementVariant,
  { background: string; color: string; iconColor: string }
> = {
  warning: {
    background: tokens.color.semantic.bg.warningSubtlest,
    color: tokens.color.semantic.text.bold,
    iconColor: tokens.color.semantic.icon.warning,
  },
  info: {
    background: tokens.color.semantic.bg.infoSubtlest,
    color: tokens.color.semantic.text.bold,
    iconColor: tokens.color.semantic.icon.info,
  },
};

interface BannerProps {
  active?: boolean;
  border?: boolean;
  variant?: AnnouncementVariant;
  children?: React.ReactNode;
}

export const Banner: React.FC<BannerProps> = ({ active, border, variant = "warning", children }) => {
  const styles = announcementVariantStyles[variant];
  return (
    <Flex.Row
      fullWidth
      justify="center"
      align="center"
      gap={16}
      sx={{
        position: "relative",
        background: styles.background,
        color: styles.color,
        height: 47,
        maxHeight: active ? 47 : 0,
        transition: "max-height ease-in-out 125ms",
        borderBottom: optObj(border, `1px solid ${styles.background}`),
      }}
    >
      {active && children}
    </Flex.Row>
  );
};

interface AnnouncementBannersProps {
  /** Runtime announcements (e.g., from backend state) prepended before config entries. */
  runtimeAnnouncements?: Announcement[];
}

export const AnnouncementBanners: React.FC<AnnouncementBannersProps> = ({ runtimeAnnouncements = [] }) => {
  const [, forceUpdate] = React.useReducer((x: number) => x + 1, 0);
  const configAnnouncements = getActiveAnnouncements();
  const visible = [...runtimeAnnouncements.filter(isNotDismissed), ...configAnnouncements];

  if (visible.length === 0) return null;

  function handleDismiss(a: Announcement) {
    dismissAnnouncement(a.id);
    forceUpdate();
  }

  return (
    <Flex.Column gap={1}>
      {visible.map((a) => {
        const variant = a.variant ?? "warning";
        return (
          <Banner key={a.id} active variant={variant}>
            <IconV3
              icon={variant === "info" ? "info" : "warning"}
              color={announcementVariantStyles[variant].iconColor}
              size={16}
            />
            <RpiText type="p3" weight="regular">
              {a.message}
            </RpiText>
            {a.link?.to && (
              <RpiLink component={Link} to={a.link.to} sx={{ color: "currentcolor" }}>
                {a.link.label}
              </RpiLink>
            )}
            {a.link?.href && (
              <RpiLink href={a.link.href} target="_blank" rel="noopener" sx={{ color: "currentcolor" }}>
                {a.link.label}
              </RpiLink>
            )}
            {a.dismissible !== false && (
              <CloseButton
                size="sm"
                sx={{ color: "currentcolor", position: "absolute", right: 16 }}
                onClick={() => handleDismiss(a)}
                aria-label="Dismiss announcement"
              />
            )}
          </Banner>
        );
      })}
    </Flex.Column>
  );
};
